import { useTranslation } from "react-i18next";
import LearningCheckin from "Assets/images/learning-checkin.svg";
import SurveyCheckin from "Assets/images/survey-checkin.svg";
import Skills360Checkin from "Assets/images/skills360-checkin.svg";
import { get, uniqBy } from "lodash";
import { useRecoilValue } from "recoil";
import { CompanyState } from "Recoil/LoggedInUserDetail/atom";
import { useTargetConstants } from "Hooks/target/useTargetConstants";
import moment from "moment";

export const useCheckinConstant = () => {
  const { t } = useTranslation();
  const _companyDetail = useRecoilValue(CompanyState);
  const { checkinMapping } = useTargetConstants();
  const checkinsPermission = get(_companyDetail, "feature.checkin", []).filter(
    (res) => res.isEnabled //&& res.type != "REQUESTFEEDBACK"
  );

  const requestFeedbackPermission = get(
    _companyDetail,
    "feature.checkin",
    []
  ).filter((res) => res.isEnabled && res.type === "REQUESTFEEDBACK");

  const checkinStepper = [
    {
      label: t("target.stepper.details"),
      wrapper: "details",
      showInTemplate: true,
    },
    {
      label: "Questions",
      wrapper: "questions",
      showInTemplate: true,
    },
    {
      label: "People",
      wrapper: "assignPeople",
      showInTemplate: false,
    },
  ];

  const checkinTypeSelection = [
    {
      id: 0,
      title: "Learning",
      type: "LEARNING",
      btnIcon: LearningCheckin,
      description: "Send an article, video, or other training",
    },
    {
      id: 1,
      title: "Survey",
      type: "SURVEY",
      btnIcon: SurveyCheckin,
      description:
        "Create a survey to gain insights, test knowledge, or collect data",
    },
    {
      id: 3,
      title: "Skill rating",
      type: "SKILLRATING",
      btnIcon: Skills360Checkin,
      description: "Ask for community feedback on skill development",
    },
  ];

  const getCheckinType = (type) => {
    const typeData = checkinTypeSelection.find((res) => res.type === type);
    return get(typeData, "title", checkinTypeSelection[0].title);
  };

  const frequencyType = [
    {
      id: "None",
      name: "None",
      value: "Does not repeat",
    },
    {
      id: "DAILY",
      name: "DAILY",
      value: "Daily",
    },
    {
      id: "WEEKLY",
      name: "WEEKLY",
      value: "Weekly",
    },
    {
      id: "MONTHLY",
      name: "MONTHLY",
      value: "Monthly",
    },
  ];

  const frequencyDays = [
    {
      id: "FIRST",
      name: "First",
      value: "First",
    },
    {
      id: "SECOND",
      name: "Second",
      value: "Second",
    },
    {
      id: "THIRD",
      name: "Third",
      value: "Third",
    },
    {
      id: "FOURTH",
      name: "Fourth",
      value: "Fourth",
    },
    {
      id: "FIFTH",
      name: "Fifth",
      value: "Last",
    },
  ];

  const monthlyFrequencyWeekDays = [
    {
      name: "Sunday",
      value: "Sunday",
    },
    {
      name: "Monday",
      value: "Monday",
    },
    {
      name: "Tuesday",
      value: "Tuesday",
    },
    {
      name: "Wednesday",
      value: "Wednesday",
    },
    {
      name: "Thursday",
      value: "Thursday",
    },
    {
      name: "Friday",
      value: "Friday",
    },
    {
      name: "Saturday",
      value: "Saturday",
    },
  ];

  const weekOfMonth = Math.abs(moment().isoWeek() - moment().date(1).isoWeek());
  const currentWeekOfMonth =
    frequencyDays[weekOfMonth - 1]?.value || frequencyDays[0]?.value;
  const frequencyWeekDays = [
    {
      value: "Sunday",
      name: "S",
      isSelected: false,
    },
    {
      value: "Monday",
      name: "M",
      isSelected: false,
    },
    {
      value: "Tuesday",
      name: "T",
      isSelected: false,
    },
    {
      value: "Wednesday",
      name: "W",
      isSelected: false,
    },
    {
      value: "Thursday",
      name: "T",
      isSelected: false,
    },
    {
      value: "Friday",
      name: "F",
      isSelected: false,
    },
    {
      value: "Saturday",
      name: "S",
      isSelected: false,
    },
  ].map((res) => {
    if (moment().format("dddd") === res?.value) {
      return { ...res, isSelected: true };
    }
    return res;
  });

  const surveyQuestionTypes = [
    {
      name: "None",
      value: "Select type",
    },
    {
      id: "SINGLECHOICE",
      name: "SINGLECHOICE",
      value: "Multiple choice",
    },
    {
      id: "YESNO",
      name: "YESNO",
      value: "Yes/No",
    },
    {
      id: "DESCRIPTIVE",
      name: "DESCRIPTIVE",
      value: "Descriptive",
    },
    {
      id: "MULTIPLECHOICE",
      name: "MULTIPLECHOICE",
      value: "Multiple selection",
    },
    {
      id: "STARRANKING",
      name: "STARRANKING",
      value: "Star ranking",
    },
    {
      id: "EMOJIRANKING",
      name: "EMOJIRANKING",
      value: "Emoji ranking",
    },
  ];

  const surveyTestQuestionTypes = [
    {
      name: "None",
      value: "Select type",
    },
    {
      id: "SINGLECHOICE",
      name: "SINGLECHOICE",
      value: "Multiple choice",
    },
    {
      id: "YESNO",
      name: "YESNO",
      value: "Yes/No",
    },
    {
      id: "MULTIPLECHOICE",
      name: "MULTIPLECHOICE",
      value: "Multiple selection",
    },
  ];

  const checkinDefaultTab = [
    {
      text: "Company templates",
      key: "Company",
    },
    {
      text: "My templates",
      key: "My",
    },
  ];
  const checkinTemplateDefaultValue = {
    defaultValues: {
      details: {
        endDate: "",
      },
      assignPeople: {
        searchText: { coachee: "", coach: "" },
        userListData: [], // individual user list
        coachListData: [], // coach list
      },
      addSkills: {
        showModal: false,
        skillsAndStrengths: [],
      },

      addCheckin: { ...checkinMapping() },
    },
  };
  const getQuestionType = (questionType) =>
    surveyQuestionTypes?.find((res) => res.id === questionType);

  const canEditMenu = [
    { value: "Manually assign", name: "Manual" },
    { value: "Check-in creator", name: "Creator" },
  ];
  const assignMenu = [
    { value: "Manually assign", name: "Manual" },
    { value: "All people", name: "AllPeople" },
    { value: "All people (Excluding coaches)", name: "AllPeopleExlCoach" },
  ];
  const canView = [
    { value: "Manually assign", name: "Manual" },
    { value: "All people", name: "AllPeople" },
    { value: "All coaches", name: "AllCoaches" },
  ];

  const handleAssignPeopleRuleName = (assignPeopleType) => {
    if (assignPeopleType === "AllPeopleExlCoach") {
      return "All people (Excluding coaches)";
    } else if (assignPeopleType === "AllPeople") {
      return "All people";
    } else if (assignPeopleType === "AllCoaches") {
      return "All coaches";
    }
  };

  const viewResult = [
    // { value: "Manually assign", name: "Manual" },
    { value: "Check-in creator", name: "Creator" },
    // { value: "All coaches", name: "AllCoaches" },
    // { value: "All people", name: "AllPeople" },
  ];
  const raterView = uniqBy([...assignMenu, ...canView], "name");
  const learningCheckinPeople = () => {
    return [
      {
        text: "Assign",
        name: "assignCoachees",
        subText: "Select who will receive the check-in",
        options: assignMenu,
      },
      {
        text: "Can view",
        subText: "Select who can view the responses",
        name: "assignViewers",
        options: canView,
      },
      {
        text: "Can edit",
        subText: "Select who can reply and edit check-in",
        name: "primaryCoach",
        options: canEditMenu,
      },
    ];
  };

  const surveyCheckinPeople = () => {
    return [
      {
        text: "Assign",
        name: "assignCoachees",
        subText: "Select who will receive the check-in",
        options: assignMenu,
      },
      {
        text: "Result",
        name: "assignViewers",
        subText: "Select who can view the responses",
        options: [...canView, ...viewResult],
      },
      {
        text: "Can edit",
        subText: "Select who can edit check-in",
        name: "primaryCoach",
        options: canEditMenu,
      },
    ];
  };
  const SRSCheckinPeople = () => {
    return [
      {
        text: "Assign",
        name: "assignCoachees",
        subText: "Select who will receive skills rating",
        options: raterView,
      },
      {
        text: "Raters",
        name: "assignRater",
        subText: "Select who will provide skills rating",
        options: uniqBy([...canView, ...viewResult], "name"),
      },
      {
        text: "Can view",
        name: "assignViewers",
        subText: "Select who can view results",
        options: uniqBy([...canEditMenu, ...canView], "name"),
      },
    ];
  };
  return {
    checkinStepper,
    checkinsPermission,
    requestFeedbackPermission,
    checkinTypeSelection,
    getCheckinType,
    frequencyType,
    frequencyDays,
    frequencyWeekDays,
    monthlyFrequencyWeekDays,
    surveyQuestionTypes,
    surveyTestQuestionTypes,
    checkinDefaultTab,
    getQuestionType,
    checkinTemplateDefaultValue,
    currentWeekOfMonth,
    learningCheckinPeople,
    surveyCheckinPeople,
    SRSCheckinPeople,
    handleAssignPeopleRuleName,
  };
};
